// What money is expected, and how firm each piece of it is.
//
// The pipeline used to be built only from accepted proposals: it listed the
// milestones of a signed proposal that had not been paid yet, and matched them
// against *paid* invoices to decide which were collected. That left an invoice
// you had actually sent in a blind spot — the strongest claim on money there
// is, already billed and awaiting payment, appeared nowhere, while the
// milestone behind it still read as if nothing had been done about it.
//
// So there are two kinds of row here. An invoice that exists and is not paid,
// and a milestone nobody has invoiced yet. They are deliberately not the same
// number: one is owed, the other is only expected.

import { PAYMENT_SPLITS } from '../pages/invoices/constants'

const DAY = 86400000

/**
 * Installments for an accepted proposal.
 *
 * Prefers the plan the client selected — those amounts were frozen at accept
 * time and are what they agreed to pay. Falls back to the legacy prose terms so
 * proposals accepted before plans existed still produce a schedule.
 */
export function planToSplits(proposal) {
  const chosen = proposal?.selected_plan
  if (chosen?.installments?.length) {
    return chosen.installments.map(row => ({
      label: row.label,
      amount: Number(row.amount) || 0,
      pct: Number(row.pct) || 0,
      triggerPhase: row.trigger || null,
      dueDate: row.dueDate || null,
    }))
  }
  const legacy = PAYMENT_SPLITS[proposal?.payment_terms]
  if (legacy) {
    return legacy.map(s => ({ ...s, amount: Math.round((Number(proposal.total) || 0) * s.pct) }))
  }
  return []
}

const isPaid = (inv) => String(inv?.status || '') === 'paid'

/** Days past due, or -1 for anything not yet due. Paid invoices are never overdue. */
export function daysOverdue(inv, now = new Date()) {
  if (!inv?.due_date || isPaid(inv)) return -1
  const due = new Date(`${String(inv.due_date).slice(0, 10)}T23:59:59`)
  if (Number.isNaN(due.getTime())) return -1
  const days = Math.floor((now.getTime() - due.getTime()) / DAY)
  return days > 0 ? days : -1
}

/**
 * Overdue is a condition, not a stored status.
 *
 * No invoice in this system has ever carried `status = 'overdue'` — it is
 * derived from the due date every time it is displayed. Anything that filters
 * or counts on the stored status alone finds nothing, forever.
 */
export function invoiceStage(inv, now = new Date()) {
  if (isPaid(inv)) return 'paid'
  if (String(inv?.status || '') === 'draft') return 'draft'
  return daysOverdue(inv, now) > 0 ? 'overdue' : 'awaiting'
}

/**
 * Which milestone, if any, an invoice was raised for.
 *
 * Matched on the label the milestone carries, then on the amount — the same
 * two passes the page has always used, only now against every invoice for the
 * client rather than the paid ones alone. An invoice can only stand for one
 * milestone, so each is claimed at most once.
 */
function matchMilestone(splits, clientInvoices, total, proposalId) {
  const used = new Set()
  // An invoice that names a *different* proposal is not this one's to claim.
  // A client can hold two proposals for the same figure — Layla Luna has
  // carried two at $2,144 — and without this the older one absorbs the
  // deposit paid against the newer.
  const candidates = clientInvoices.filter(inv => !inv.proposal_id || inv.proposal_id === proposalId)
  const linked = candidates.filter(inv => inv.proposal_id === proposalId)
  const unlinked = candidates.filter(inv => !inv.proposal_id)

  return splits.map(split => {
    const amount = Math.round(split.amount ?? ((Number(total) || 0) * split.pct))
    const label = String(split.label || '').toLowerCase()
    const named = (inv) => (inv.description || inv.title || '').toLowerCase().includes(label)
    // Tolerant on amount, because a milestone is a percentage and an invoice is
    // a rounded figure with work sometimes added to it.
    const sized = (inv) => Math.abs(parseFloat(inv.amount) - amount) <= Math.max(5, amount * 0.15)
    const free = (inv) => !used.has(inv.id)

    // A linked invoice beats an unlinked one on either test, so the guess is
    // only ever used where the link is still missing.
    const match =
      linked.find(inv => free(inv) && named(inv)) ||
      linked.find(inv => free(inv) && sized(inv)) ||
      unlinked.find(inv => free(inv) && named(inv)) ||
      unlinked.find(inv => free(inv) && sized(inv)) ||
      null

    if (match) used.add(match.id)
    return { split, amount, invoice: match }
  })
}

/**
 * Everything still expected, most urgent first.
 *
 * Rows are one of:
 *   kind 'invoice'   — raised and unpaid. `stage` is overdue | awaiting | draft.
 *   kind 'milestone' — agreed in a signed proposal, no invoice raised for it.
 */
export function buildPipeline({ invoices = [], proposals = [], now = new Date() } = {}) {
  const rows = []
  const claimedByMilestone = new Set()

  // Milestones first, so an invoice that stands for one is labelled with it
  // rather than appearing as an unexplained charge.
  for (const prop of proposals) {
    if (String(prop?.status || '') !== 'accepted') continue
    const splits = planToSplits(prop)
    if (!splits.length || !prop.total) continue

    const clientInvoices = invoices
      .filter(i => i.client_id === prop.client_id)
      .sort((a, b) => String(a.issue_date || a.created_at || '').localeCompare(String(b.issue_date || b.created_at || '')))

    for (const { split, amount, invoice } of matchMilestone(splits, clientInvoices, prop.total, prop.id)) {
      if (invoice) {
        claimedByMilestone.add(invoice.id)
        if (isPaid(invoice)) continue
        rows.push({
          kind: 'invoice',
          id: invoice.id,
          stage: invoiceStage(invoice, now),
          clientId: prop.client_id,
          clientName: prop.clients?.name || 'Unknown',
          company: prop.clients?.company || '',
          projectTitle: prop.title,
          milestone: split.label,
          label: invoice.invoice_number || invoice.description || split.label,
          amount: parseFloat(invoice.amount) || 0,
          dueDate: invoice.due_date || null,
          daysOverdue: daysOverdue(invoice, now),
          pct: split.pct,
        })
        continue
      }
      rows.push({
        kind: 'milestone',
        id: `${prop.id}:${split.label}`,
        stage: 'uninvoiced',
        clientId: prop.client_id,
        clientName: prop.clients?.name || 'Unknown',
        company: prop.clients?.company || '',
        projectTitle: prop.title,
        milestone: split.label,
        label: split.label,
        amount,
        dueDate: split.dueDate || null,
        daysOverdue: -1,
        pct: split.pct,
      })
    }
  }

  // Every other unpaid invoice — time billed, a one-off, a retainer. These
  // belong to no proposal, which is exactly why they were invisible before.
  for (const inv of invoices) {
    if (isPaid(inv) || claimedByMilestone.has(inv.id)) continue
    rows.push({
      kind: 'invoice',
      id: inv.id,
      stage: invoiceStage(inv, now),
      clientId: inv.client_id,
      clientName: inv.clients?.name || 'Unknown',
      company: inv.clients?.company || '',
      projectTitle: null,
      milestone: null,
      label: inv.invoice_number || inv.description || 'Invoice',
      amount: parseFloat(inv.amount) || 0,
      dueDate: inv.due_date || null,
      daysOverdue: daysOverdue(inv, now),
      pct: null,
    })
  }

  // Overdue money first, then what is billed and waiting, then drafts, then
  // work not yet invoiced — which is the order it needs chasing in.
  const rank = { overdue: 0, awaiting: 1, draft: 2, uninvoiced: 3 }
  rows.sort((a, b) => {
    if (rank[a.stage] !== rank[b.stage]) return rank[a.stage] - rank[b.stage]
    if (a.stage === 'overdue') return b.daysOverdue - a.daysOverdue
    const ad = a.dueDate || '9999-12-31'
    const bd = b.dueDate || '9999-12-31'
    if (ad !== bd) return ad.localeCompare(bd)
    return b.amount - a.amount
  })

  return rows
}

/**
 * The pipeline split by how firm the money is.
 *
 * `invoiced` is owed today. `uninvoiced` is agreed but nobody has asked for it
 * yet, so it is a prompt to raise an invoice rather than money to count on.
 * Drafts are separate again — a draft has not been sent to anyone.
 */
export function pipelineTotals(rows = []) {
  const sum = (pred) => rows.filter(pred).reduce((s, r) => s + (r.amount || 0), 0)
  return {
    overdue:    sum(r => r.stage === 'overdue'),
    awaiting:   sum(r => r.stage === 'awaiting'),
    draft:      sum(r => r.stage === 'draft'),
    uninvoiced: sum(r => r.stage === 'uninvoiced'),
    invoiced:   sum(r => r.kind === 'invoice' && r.stage !== 'draft'),
    total:      sum(() => true),
    counts: {
      overdue:    rows.filter(r => r.stage === 'overdue').length,
      awaiting:   rows.filter(r => r.stage === 'awaiting').length,
      draft:      rows.filter(r => r.stage === 'draft').length,
      uninvoiced: rows.filter(r => r.stage === 'uninvoiced').length,
    },
  }
}

/* ── What is owed, defined once ──────────────────────────────────────────────
 *
 * Three pages showed a figure labelled "Uncollected" or "Outstanding" and all
 * three disagreed, on the same screen:
 *
 *   Invoices page   $0      not paid and not draft — billed, waiting
 *   Dashboard       $600    not paid, drafts included
 *   Sidebar         $2,247  not paid, plus accepted proposals not yet invoiced
 *
 * The Invoices page was right and said so in a comment: a draft has been shown
 * to nobody, so counting it overstates what a client owes. The dashboard
 * counted drafts. The sidebar added the value of accepted work that has not
 * been invoiced at all, which is pipeline, not receivables — and is already
 * reported separately as Pipeline Value, so it was being counted twice.
 *
 * Money is the thing a portal has to be right about. These are the definitions;
 * nothing should compute them inline again.
 */

const amount = i => parseFloat(i?.amount) || 0

/** Billed and waiting to be paid. Excludes drafts, which nobody has seen. */
export function outstandingTotal(invoices = []) {
  return invoices.filter(i => i.status !== 'paid' && i.status !== 'draft').reduce((s, i) => s + amount(i), 0)
}

/** Written but not sent. Real work, but not yet anybody's debt. */
export function draftTotal(invoices = []) {
  return invoices.filter(i => i.status === 'draft').reduce((s, i) => s + amount(i), 0)
}

/** Money actually received. */
export function collectedTotal(invoices = []) {
  return invoices.filter(i => i.status === 'paid').reduce((s, i) => s + amount(i), 0)
}

/** Everything ever invoiced, drafts included. */
export function billedTotal(invoices = []) {
  return invoices.reduce((s, i) => s + amount(i), 0)
}

/** Billed and sent. A draft is work written down, not money asked for. */
export function billedSentTotal(invoices = []) {
  return invoices.filter(i => i.status !== 'draft').reduce((s, i) => s + amount(i), 0)
}

/* ── What is owed, when an invoice was never raised ──────────────────────────
 *
 * `outstandingTotal` answers "which invoices are waiting to be paid". On this
 * agency's books that came to $0, because 39 of 40 invoices were paid and the
 * fortieth was a draft — while four accepted engagements carried $7,959 that
 * had not been paid, three of which had never been invoiced at all.
 *
 * Both figures are true. Only one of them is what an owner means by
 * "uncollected", so this counts money owed rather than paper issued:
 *
 *   a project with an agreed price   its price, less what has been paid on it
 *   an accepted proposal without one its total, less what has been paid on it
 *   an unpaid invoice against neither its amount
 *
 * Netting against the agreed price is what keeps it honest: a $3,450 project
 * with a $1,725 deposit paid owes $1,725 — not $3,450 plus the deposit, and
 * not $0 because the one invoice raised was settled.
 */

const money = (v) => {
  const n = parseFloat(v)
  return Number.isFinite(n) ? n : 0
}

const owedOn = (contract, paid) => Math.max(0, contract - paid)

/**
 * Everything owed to the agency, and how much of it has actually been billed.
 *
 * `invoiced` is the part a client has been asked for; `uninvoiced` is agreed
 * work nobody has sent a bill for. They add up to `total`, so a tile can show
 * one number and explain itself in a line.
 */
export function amountOwed({ projects = [], proposals = [], invoices = [] } = {}) {
  const rows = invoices || []
  const spoken = new Set()   // invoice ids already accounted for by an engagement
  const engagements = []

  const claim = (list) => list.forEach(i => { if (i?.id) spoken.add(i.id) })

  const paidOf   = (list) => list.filter(i => i.status === 'paid').reduce((s, i) => s + amount(i), 0)
  const billedOf = (list) => list.filter(i => i.status !== 'paid' && i.status !== 'draft').reduce((s, i) => s + amount(i), 0)

  const priced = (projects || []).filter(p => money(p?.contract_value) > 0)
  const pricedProposalIds = new Set(priced.map(p => p?.proposal_id).filter(Boolean))

  priced.forEach(project => {
    const mine = rows.filter(i => i?.project_id && i.project_id === project.id)
    claim(mine)
    const contract = money(project.contract_value)
    const paid = paidOf(mine)
    const owed = owedOn(contract, paid)
    if (owed <= 0) return
    // The billed share of what is owed cannot exceed the debt itself.
    const billed = Math.min(owed, billedOf(mine))
    engagements.push({
      id: project.id, label: project.title || 'Untitled project', kind: 'project',
      contract, paid, owed, invoiced: billed, uninvoiced: owed - billed,
    })
  })

  // An accepted proposal that never became a priced project still carries the
  // number the client agreed to. Two of them were carrying $2,994 that no
  // other row in the database knew about.
  ;(proposals || [])
    .filter(p => p?.status === 'accepted' && !pricedProposalIds.has(p?.id))
    .forEach(proposal => {
      const mine = rows.filter(i => i?.proposal_id && i.proposal_id === proposal.id)
      claim(mine)
      const contract = money(proposal.total)
      // amount_paid is kept current by the invoice trigger; fall back to the
      // invoices themselves when it has not been written yet.
      const paid = Math.max(money(proposal.amount_paid), paidOf(mine))
      const owed = owedOn(contract, paid)
      if (owed <= 0) return
      const billed = Math.min(owed, billedOf(mine))
      engagements.push({
        id: proposal.id, label: proposal.title || proposal.proposal_number || 'Accepted proposal', kind: 'proposal',
        contract, paid, owed, invoiced: billed, uninvoiced: owed - billed,
      })
    })

  // Anything billed and unpaid that belongs to no engagement above — one-off
  // work, invoiced directly. Counted once, at face value.
  const loose = rows.filter(i =>
    !spoken.has(i?.id) && i?.status !== 'paid' && i?.status !== 'draft',
  )
  loose.forEach(i => {
    engagements.push({
      id: i.id, label: i.description || i.invoice_number || 'Invoice', kind: 'invoice',
      contract: amount(i), paid: 0, owed: amount(i), invoiced: amount(i), uninvoiced: 0,
    })
  })

  const sum = (key) => engagements.reduce((s, e) => s + e[key], 0)
  return {
    total: sum('owed'),
    invoiced: sum('invoiced'),
    uninvoiced: sum('uninvoiced'),
    engagements: engagements.sort((a, b) => b.owed - a.owed),
  }
}

/** The line under the number, so a total never has to be taken on trust. */
export function describeOwed(owed) {
  if (!owed || owed.total <= 0) return 'Nothing outstanding'
  const dollars = (n) => `$${Math.round(n).toLocaleString('en-US')}`
  if (owed.uninvoiced <= 0) return `${dollars(owed.invoiced)} invoiced, waiting`
  if (owed.invoiced <= 0) return `${dollars(owed.uninvoiced)} agreed, not yet invoiced`
  return `${dollars(owed.invoiced)} invoiced · ${dollars(owed.uninvoiced)} not yet invoiced`
}
